import { useState } from 'react';
import React from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Card, CardBody, Layer, Text } from 'grommet';
import { ApiError } from '/src/api';
import { Input } from '/src/components';
import { CardHeader } from '/src/components';
import { toastMessages } from '/src/lib/toast';

interface StripeAccount {
  id: string;
  email: string;
}

interface SendMoneyModalProps {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  selectedAccount: StripeAccount | null;
}

export const SendMoneyModal = ({ isVisible, setIsVisible, selectedAccount }: SendMoneyModalProps) => {
  const [amount, setAmount] = useState<string>('');
  const [isSendingMoney, setIsSendingMoney] = useState(false);

  const formatAmount = (value: string): string => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }

    if (parts.length === 2 && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].slice(0, 2);
    }
    return numericValue;
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatAmount(event.target.value);
    setAmount(formattedValue);
  };

  const handleSendMoney = async () => {
    if (!selectedAccount || !amount) {
      toast.error(toastMessages.moneyStripeTransfer.missingInfo);
      return;
    }

    setIsSendingMoney(true);
    try {
      const amountInCents = parseFloat(amount) * 100;
      if (isNaN(amountInCents) || amountInCents <= 0) {
        toast.error(toastMessages.moneyStripeTransfer.invalidAmount);
        setIsSendingMoney(false);
        return;
      }

      toast.success(toastMessages.moneyStripeTransfer.success);
      setIsVisible(false);
      setAmount('');
    } catch (error) {
      console.error('Error sending money:', error);
      toast.error(toastMessages.moneyStripeTransfer.error(error as ApiError));
    } finally {
      setIsSendingMoney(false);
    }
  };

  if (!isVisible) return null;

  return (
    <Layer
      onEsc={() => setIsVisible(false)}
      onClickOutside={() => setIsVisible(false)}
      position="center"
      modal
      background="transparent"
    >
      <Card width="100%" style={{ maxWidth: 480, minWidth: 320 }}>
        <CardHeader title="Send Money" />
        <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
          <Box gap="medium">
            <Text size="small">Selected Account ID:</Text>
            <Text weight="bold" size="medium">
              {selectedAccount?.id}
            </Text>

            {/* <Text size="small" margin={{ top: 'small' }}>Bank Account ID:</Text>
            {loadingBankAccount ? (
              <Box align="center" justify="center">
                <Spinner size="small" />
              </Box>
            ) : (
              <Text weight="bold" size="medium">{bankAccountId}</Text>
            )} */}

            <Box margin={{ top: 'small' }}>
              <Input
                label="Amount (USD):"
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                required
                min="0.01"
                step="0.01"
                prefix="$"
              />
            </Box>

            <Box direction="row" gap="small" justify="end">
              <Button
                label="Cancel"
                onClick={() => setIsVisible(false)}
                disabled={isSendingMoney}
                style={{ fontWeight: 'normal' }}
              />
              <Button
                primary
                label={isSendingMoney ? 'Sending...' : 'Send Money'}
                onClick={handleSendMoney}
                disabled={isSendingMoney || !selectedAccount || !amount || parseFloat(amount) <= 0}
                style={{ fontWeight: 'normal' }}
              />
            </Box>
          </Box>
        </CardBody>
      </Card>
    </Layer>
  );
};
